import { storm as presetStorm } from '../sim/presets.ts';
import { temperatureAt } from '../sim/temperature.ts';
import { totalHours, type StormTimelineEvent, type TimelineEvent } from '../sim/timeline.ts';
import { DEFAULT_WEAK_LAYERS } from '../sim/weaklayers.ts';
import { conditions, terrain } from '../state/store.ts';
import { formatCompass16, round } from './format.ts';
import { NumberField } from './NumberField.tsx';
import { PresetPicker } from './PresetPicker.tsx';
import { WindCompassRose } from './WindCompassRose.tsx';

/** The storm to show in simple mode: the one with the most new snow (a scenario can have several
 * — e.g. rain-on-snow — but simple mode only edits the headline one). */
function largestStorm(events: readonly TimelineEvent[]): StormTimelineEvent | null {
  let best: StormTimelineEvent | null = null;
  for (const e of events) {
    if (e.kind === 'storm' && (!best || e.newSnowCm > best.newSnowCm)) best = e;
  }
  return best;
}

/**
 * Simple mode's "Conditions" section: a preset picker, existing snow + a buried-weak-layer
 * switch, and a short editor for the scenario's main storm (new snow, duration, wind,
 * temperature). The timeline bar (over the 3D view) replaces the old "hours after storm" slider —
 * simple mode never edits the event list directly.
 */
export function SimpleConditionsPanel() {
  const t = terrain.value;
  if (!t) return null;
  const c = conditions.value;
  const valleyElevation = t.minElevation;
  const summitElevation = t.maxElevation;
  const storm = largestStorm(c.events);

  function updateMainStorm(patch: Partial<StormTimelineEvent>): void {
    const cur = conditions.peek();
    const main = largestStorm(cur.events);
    if (!main) return;
    const next = cur.events.map((e) => (e.id === main.id ? { ...main, ...patch } : e));
    const nextTotal = totalHours(next);
    conditions.value = {
      ...conditions.value,
      events: next,
      evaluationHour: Math.min(Math.max(0, cur.evaluationHour), nextTotal),
    };
  }

  function addMainStorm(): void {
    const cur = conditions.peek();
    const e = presetStorm(24, 30, 12, 270, cur.temperature);
    const next = [...cur.events, e];
    const nextTotal = totalHours(next);
    conditions.value = {
      ...conditions.value,
      events: next,
      evaluationHour: Math.min(Math.max(0, cur.evaluationHour), nextTotal),
    };
  }

  function setTemps(nextValley: number, nextSummit: number): void {
    updateMainStorm({
      temperature: {
        lowElevation: valleyElevation,
        lowTemp: nextValley,
        highElevation: summitElevation,
        highTemp: nextSummit,
      },
    });
  }

  const weakLayersOn = c.weakLayers.length > 0 && c.weakLayers.every((w) => w.enabled);
  function setWeakLayersOn(on: boolean): void {
    const current = conditions.value.weakLayers;
    const base = on && current.length === 0 ? DEFAULT_WEAK_LAYERS : current;
    conditions.value = {
      ...conditions.value,
      weakLayers: base.map((w) => ({ ...w, enabled: on })),
    };
  }

  return (
    <>
      <PresetPicker />

      <section class="panel-section">
        <h2>Conditions</h2>
        <NumberField
          label="Existing snow depth"
          value={c.initialSnow.referenceDepth * 100}
          min={0}
          max={500}
          step={5}
          unit="cm"
          onChange={(v) =>
            (conditions.value = {
              ...conditions.value,
              initialSnow: { ...conditions.value.initialSnow, referenceDepth: v / 100 },
            })
          }
        />
        <label class="checkbox-field">
          <input
            type="checkbox"
            checked={weakLayersOn}
            onChange={(e) => setWeakLayersOn((e.target as HTMLInputElement).checked)}
          />
          Buried weak layer (surface hoar on shaded slopes)
        </label>
      </section>

      <section class="panel-section">
        <h2>Main storm</h2>
        {storm ? (
          <>
            <NumberField
              label="New snow"
              value={storm.newSnowCm}
              min={0}
              max={200}
              step={1}
              unit="cm"
              onChange={(v) => updateMainStorm({ newSnowCm: v })}
            />
            <NumberField
              label="Duration"
              value={storm.durationHours}
              min={1}
              max={120}
              step={1}
              unit="h"
              onChange={(v) => updateMainStorm({ durationHours: v })}
            />
            <NumberField
              label="Wind speed"
              value={storm.windSpeed}
              min={0}
              max={30}
              step={0.5}
              unit="m/s"
              decimals={1}
              onChange={(v) => updateMainStorm({ windSpeed: v })}
            />
            <div class="wind-rose">
              <WindCompassRose
                deg={storm.windFromDeg}
                onChange={(v) => updateMainStorm({ windFromDeg: v })}
              />
              <div class="wind-rose-info">
                <span class="compass-name">Wind from {formatCompass16(storm.windFromDeg)}</span>
              </div>
            </div>
            <NumberField
              label={`Valley (at ${round(valleyElevation)} m)`}
              value={temperatureAt(storm.temperature, valleyElevation)}
              min={-40}
              max={20}
              step={0.5}
              unit="°C"
              decimals={1}
              onChange={(v) => setTemps(v, temperatureAt(storm.temperature, summitElevation))}
            />
            <NumberField
              label={`Summit (at ${round(summitElevation)} m)`}
              value={temperatureAt(storm.temperature, summitElevation)}
              min={-40}
              max={20}
              step={0.5}
              unit="°C"
              decimals={1}
              onChange={(v) => setTemps(temperatureAt(storm.temperature, valleyElevation), v)}
            />
          </>
        ) : (
          <button type="button" class="primary" onClick={addMainStorm}>
            Add a storm
          </button>
        )}
      </section>
    </>
  );
}
